import { createApi } from '@reduxjs/toolkit/query/react';
import { AxiosError } from 'axios';
import {
  AddressValidationRequest,
  AddressValidationResult,
  AsceLoadResult,
  Building,
  BuildingRequest,
  BuildingWithTrusses,
  BuildingWithoutTrusses,
  CheckoutRequest,
  CheckoutResponse,
  CheckoutValidationResponse,
  CloneBuildingRequest,
  CoordinatesValidationRequest,
  DashboardKpiRequest,
  DashboardKpiResponse,
  DashboardMonthlyRequest,
  DashboardMonthlyResponse,
  Order,
  OrderDueDateUpdateRequest,
  OrderFilters,
  OrderListingResponse,
  OrderSignRequest,
  OrderStatus,
  OrdersDueReportResponse,
  OrdersPastDueReportResponse,
  PaginatedResponse,
  PaginationParams,
  ProductsReportResponse,
  ReverseGeocodeResult,
  SortParams,
  TravelerCompletionRequest,
} from '../../types';
import { baseQueryStub, shouldRefetchOnSortChange, transformOrderFromAPI } from '../../utils';
import { AddressValidationService } from './address-validation-service';
import { AsceService } from './asce-service';
import { OrderService } from './order-service';

let orderService: OrderService | null = null;
const getOrderService = () => {
  orderService ??= new OrderService();
  return orderService;
};

let asceService: AsceService | null = null;
const getAsceService = () => {
  asceService ??= new AsceService();
  return asceService;
};

let addressValidationService: AddressValidationService | null = null;
const getAddressValidationService = () => {
  addressValidationService ??= new AddressValidationService();
  return addressValidationService;
};

/**
 * Converts caught errors to RTK Query error format, preserving response.data
 * so that getErrorMessage() can extract the correct validation message from the API response.
 * Without this, RTK Query serializes errors and loses response.data, causing
 * generic 'An unexpected error occurred.' messages instead of specific validation messages.
 */
const handleQueryFnError = (error: unknown): { error: { status: number; data: unknown } } => {
  if (error instanceof AxiosError && error.response) {
    return {
      error: {
        status: error.response.status,
        data: error.response.data,
      },
    };
  }
  return {
    error: {
      status: 500,
      data: { message: error instanceof Error ? error.message : 'An unexpected error occurred.' },
    },
  };
};

export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: baseQueryStub,
  tagTypes: ['Order', 'OrderBuilding', 'Dashboard'],
  serializeQueryArgs: ({ queryArgs, endpointName }) => {
    if (endpointName === 'getOrders' || endpointName === 'getAllOrders') {
      const { pagination, filters, sort } = queryArgs as {
        pagination: PaginationParams;
        filters?: OrderFilters;
        sort?: SortParams;
      };

      return JSON.stringify({
        endpointName,
        pagination,
        filters,
        sort,
      });
    }

    return JSON.stringify({ endpointName, queryArgs });
  },
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: builder => ({
    createOrder: builder.mutation<OrderListingResponse, { orderData: Omit<Order, 'id'> }>({
      queryFn: async ({ orderData }) => {
        const service = getOrderService();
        const newOrder = await service.createOrder(orderData);
        return { data: newOrder };
      },
      invalidatesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    updateOrder: builder.mutation<void, Order & { id: number }>({
      queryFn: async order => {
        const service = getOrderService();
        await service.updateOrder(order.id, order);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Order', id },
        { type: 'Order', id: id.toString() },
        { type: 'Order', id: 'LIST' },
      ],
    }),
    confirmTrussStateChange: builder.mutation<
      void,
      { orderId: number; trussId: number; buildingId?: number }
    >({
      queryFn: async ({ orderId, trussId }) => {
        const service = getOrderService();
        await service.confirmTrussStateChange(orderId, trussId);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { orderId, buildingId }) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: orderId.toString() },
        ...(buildingId
          ? [{ type: 'OrderBuilding' as const, id: buildingId.toString() }]
          : [{ type: 'OrderBuilding' as const, id: 'LIST' }]),
      ],
    }),
    confirmAllTrussStateChanges: builder.mutation<void, { orderId: number; buildingId?: number }>({
      queryFn: async ({ orderId }) => {
        const service = getOrderService();
        await service.confirmAllTrussStateChanges(orderId);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { orderId, buildingId }) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: orderId.toString() },
        ...(buildingId
          ? [{ type: 'OrderBuilding' as const, id: buildingId.toString() }]
          : [{ type: 'OrderBuilding' as const, id: 'LIST' }]),
      ],
    }),
    getOrders: builder.query<
      PaginatedResponse<OrderListingResponse>,
      {
        pagination: PaginationParams;
        filters?: OrderFilters;
        sort?: SortParams;
      }
    >({
      queryFn: async ({ pagination, filters, sort }) => {
        const { page, pageSize } = pagination;
        const service = getOrderService();
        const response = await service.getOrders(page, pageSize, filters, sort);
        return { data: response };
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Order' as const, id })),
              { type: 'Order', id: 'LIST' },
            ]
          : [{ type: 'Order', id: 'LIST' }],
      keepUnusedDataFor: 30, // 30 seconds
      forceRefetch: shouldRefetchOnSortChange,
    }),

    getAllOrders: builder.query<
      PaginatedResponse<OrderListingResponse>,
      {
        pagination: PaginationParams;
        filters?: OrderFilters;
        sort?: SortParams;
      }
    >({
      queryFn: async ({ pagination, filters, sort }) => {
        const { page, pageSize } = pagination;
        const service = getOrderService();
        const response = await service.getOrders(page, pageSize, filters, sort);
        return { data: response };
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Order' as const, id })),
              { type: 'Order', id: 'LIST' },
            ]
          : [{ type: 'Order', id: 'LIST' }],
      keepUnusedDataFor: 60, // 60 seconds
    }),

    getOrderById: builder.query<Order, string>({
      queryFn: async id => {
        const service = getOrderService();
        const order = await service.getOrderById(id);
        return { data: transformOrderFromAPI(order) };
      },
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),

    getOrdersByManufacturerId: builder.query<OrderListingResponse[], string>({
      queryFn: async manufacturerId => {
        const service = getOrderService();
        const orders = await service.getOrdersByManufacturerId(manufacturerId);
        return { data: orders };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Order' as const, id })),
              { type: 'Order', id: 'LIST' },
            ]
          : [{ type: 'Order', id: 'LIST' }],
    }),

    getOrdersByDealerId: builder.query<OrderListingResponse[], string>({
      queryFn: async dealerId => {
        const service = getOrderService();
        const orders = await service.getOrdersByDealerId(dealerId);
        return { data: orders };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Order' as const, id })),
              { type: 'Order', id: 'LIST' },
            ]
          : [{ type: 'Order', id: 'LIST' }],
    }),

    validatePOUniqueness: builder.query<{ isUnique: boolean }, { dealerId: number; po: string }>({
      queryFn: async ({ dealerId, po }) => {
        const service = getOrderService();
        const isUnique = await service.validatePOUniqueness(dealerId, po);
        return { data: { isUnique } };
      },
      // Don't cache validation results - always fetch fresh data
      keepUnusedDataFor: 0,
    }),

    // Building endpoints
    getBuildingsByOrderId: builder.query<BuildingWithoutTrusses[], string>({
      queryFn: async orderId => {
        const service = getOrderService();
        const buildings = await service.getBuildingsByOrderId(orderId);
        return { data: buildings };
      },
      providesTags: (result, _error, orderId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'OrderBuilding' as const, id })),
              { type: 'OrderBuilding', id: `ORDER-${orderId}` },
            ]
          : [{ type: 'OrderBuilding', id: `ORDER-${orderId}` }],
    }),

    getBuildingById: builder.query<BuildingWithTrusses, string>({
      queryFn: async buildingId => {
        const service = getOrderService();
        const building = await service.getBuildingById(buildingId);
        return { data: building };
      },
      providesTags: (_result, _error, buildingId) => [{ type: 'OrderBuilding', id: buildingId }],
    }),

    addBuildingToOrder: builder.mutation<Building, { orderId: number; building: BuildingRequest }>({
      queryFn: async ({ orderId, building }) => {
        try {
          const service = getOrderService();
          const newBuilding = await service.addBuildingToOrder(orderId, building);
          return { data: newBuilding };
        } catch (error) {
          return handleQueryFnError(error);
        }
      },
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderBuilding', id: `ORDER-${orderId}` },
        { type: 'Order', id: orderId.toString() },
      ],
    }),

    updateBuilding: builder.mutation<void, { buildingId: number; building: BuildingRequest }>({
      queryFn: async ({ buildingId, building }) => {
        try {
          const service = getOrderService();
          await service.updateBuilding(buildingId, building);
          return { data: undefined };
        } catch (error) {
          return handleQueryFnError(error);
        }
      },
      invalidatesTags: (_result, _error, { buildingId }) => [
        { type: 'OrderBuilding', id: buildingId },
        // getBuildingById is keyed by the string id (useGetBuildingByIdQuery passes
        // selectedBuildingId.toString()). RTK Query matches tag ids by strict value, so the
        // numeric tag above never invalidates the detail query. Without this string form the
        // selected building stays stale after Update Cart, leaving Building Total and Totals by
        // Truss Type out of sync while the list-derived Cart Total updates (UDOMI-969).
        { type: 'OrderBuilding', id: buildingId.toString() },
        { type: 'OrderBuilding', id: 'LIST' },
      ],
    }),

    deleteBuilding: builder.mutation<void, number>({
      queryFn: async buildingId => {
        const service = getOrderService();
        await service.deleteBuilding(buildingId);
        return { data: undefined };
      },
      // Do not invalidate the deleted building id: that refetches getBuildingById while the
      // manage-buildings page can still be subscribed until React applies cleared selection → 404 toast.
      invalidatesTags: () => [{ type: 'OrderBuilding', id: 'LIST' }],
    }),

    cloneBuilding: builder.mutation<Building, CloneBuildingRequest>({
      queryFn: async cloneRequest => {
        const service = getOrderService();
        const clonedBuilding = await service.cloneBuilding(cloneRequest);
        return { data: clonedBuilding };
      },
      invalidatesTags: (_result, _error, { targetOrderId }) => [
        { type: 'OrderBuilding', id: `ORDER-${targetOrderId}` },
        { type: 'Order', id: targetOrderId.toString() },
      ],
    }),

    // Dashboard endpoints
    getMonthlyDashboard: builder.query<DashboardMonthlyResponse, DashboardMonthlyRequest>({
      queryFn: async request => {
        const service = getOrderService();
        const response = await service.getMonthlyDashboard(request);
        return { data: response };
      },
      providesTags: ['Dashboard'],
      keepUnusedDataFor: 30, // 30 seconds
    }),

    getDashboardKpi: builder.query<DashboardKpiResponse, DashboardKpiRequest>({
      queryFn: async request => {
        const service = getOrderService();
        const response = await service.getDashboardKpi(request);
        return { data: response };
      },
      providesTags: ['Dashboard'],
      keepUnusedDataFor: 60, // 60 seconds - KPI data doesn't change as frequently
    }),

    updateOrderDueDate: builder.mutation<
      void,
      { orderId: number; request: OrderDueDateUpdateRequest }
    >({
      queryFn: async ({ orderId, request }) => {
        const service = getOrderService();
        await service.updateOrderDueDate(orderId, request);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { orderId }) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'Dashboard' },
      ],
    }),

    placeOrderOnHold: builder.mutation<void, number>({
      queryFn: async orderId => {
        const service = getOrderService();
        await service.placeOrderOnHold(orderId);
        return { data: undefined };
      },
      invalidatesTags: (_, __, orderId) => [
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'Dashboard' },
      ],
    }),

    shipOrder: builder.mutation<void, number>({
      queryFn: async orderId => {
        const service = getOrderService();
        await service.shipOrder(orderId);
        return { data: undefined };
      },
      invalidatesTags: (_, __, orderId) => [
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'Dashboard' },
      ],
    }),

    releaseForPickup: builder.mutation<void, number>({
      queryFn: async orderId => {
        const service = getOrderService();
        await service.releaseForPickup(orderId);
        return { data: undefined };
      },
      invalidatesTags: (_, __, orderId) => [
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'Dashboard' },
      ],
    }),

    releaseForShipment: builder.mutation<void, number>({
      queryFn: async orderId => {
        const service = getOrderService();
        await service.releaseForShipment(orderId);
        return { data: undefined };
      },
      invalidatesTags: (_, __, orderId) => [
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'Dashboard' },
      ],
    }),

    getOrdersDueReport: builder.query<
      OrdersDueReportResponse,
      { startDate: string; endDate: string; dealerId?: number; manufacturerId?: number }
    >({
      queryFn: async ({ startDate, endDate, dealerId, manufacturerId }) => {
        const service = getOrderService();
        const response = await service.getOrdersDueReport(
          startDate,
          endDate,
          dealerId,
          manufacturerId
        );
        return { data: response };
      },
      providesTags: ['Order'],
      keepUnusedDataFor: 30,
    }),

    getOrdersPastDueReport: builder.query<
      OrdersPastDueReportResponse,
      { dealerId?: number; manufacturerId?: number }
    >({
      queryFn: async ({ dealerId, manufacturerId }) => {
        const service = getOrderService();
        const response = await service.getOrdersPastDueReport(dealerId, manufacturerId);
        return { data: response };
      },
      providesTags: ['Order'],
      keepUnusedDataFor: 30,
    }),

    getProductsReport: builder.query<
      ProductsReportResponse,
      { manufacturerId?: number; trussType?: string; orderDateFrom?: string; orderDateTo?: string }
    >({
      queryFn: async ({ manufacturerId, trussType, orderDateFrom, orderDateTo }) => {
        const service = getOrderService();
        const response = await service.getProductsReport(
          manufacturerId,
          trussType,
          orderDateFrom,
          orderDateTo
        );
        return { data: response };
      },
      providesTags: ['Order'],
      keepUnusedDataFor: 60,
    }),

    signOrder: builder.mutation<void, { orderId: number; request: OrderSignRequest }>({
      queryFn: async ({ orderId, request }) => {
        const service = getOrderService();
        await service.signOrder(orderId, request);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { orderId }) => [{ type: 'Order', id: orderId.toString() }],
    }),

    updateTravelerCompletion: builder.mutation<
      void,
      { orderId: number; request: TravelerCompletionRequest }
    >({
      queryFn: async ({ orderId, request }) => {
        const service = getOrderService();
        await service.updateTravelerCompletion(orderId, request);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { orderId }) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: orderId.toString() },
      ],
    }),

    validateCheckout: builder.mutation<CheckoutValidationResponse, number>({
      queryFn: async orderId => {
        const service = getOrderService();
        const response = await service.validateCheckout(orderId);
        return { data: response };
      },
    }),

    checkoutOrder: builder.mutation<
      CheckoutResponse,
      { orderId: number; request?: CheckoutRequest }
    >({
      queryFn: async ({ orderId, request }) => {
        const service = getOrderService();
        const response = await service.checkoutOrder(orderId, request);
        return { data: response };
      },
      invalidatesTags: (_, __, { orderId }) => [
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderBuilding', id: `ORDER-${orderId}` },
        { type: 'Dashboard' },
      ],
    }),

    returnOrderToDraft: builder.mutation<void, number>({
      queryFn: async orderId => {
        const service = getOrderService();
        await service.updateOrderStatus(orderId, OrderStatus.DRAFT);
        return { data: undefined };
      },
      invalidatesTags: (_, __, orderId) => [
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'Dashboard' },
      ],
    }),

    releaseToProduction: builder.mutation<void, number>({
      queryFn: async orderId => {
        const service = getOrderService();
        await service.releaseToProduction(orderId);
        return { data: undefined };
      },
      invalidatesTags: (_, __, orderId) => [
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'Dashboard' },
      ],
    }),

    releaseOrderFromHold: builder.mutation<void, number>({
      queryFn: async orderId => {
        const service = getOrderService();
        await service.releaseOrderFromHold(orderId);
        return { data: undefined };
      },
      invalidatesTags: (_, __, orderId) => [
        { type: 'Order', id: orderId.toString() },
        { type: 'Order', id: 'LIST' },
        { type: 'Dashboard' },
      ],
    }),

    // ASCE API endpoints
    getAsceLoadRatings: builder.mutation<
      AsceLoadResult,
      { lat: number; lon: number; riskLevel?: number }
    >({
      queryFn: async ({ lat, lon, riskLevel = 1 }) => {
        try {
          const service = getAsceService();
          const result = await service.getLoadRatings(lat, lon, riskLevel);
          return { data: result };
        } catch (error) {
          return handleQueryFnError(error);
        }
      },
    }),

    // Address Validation endpoints
    validateAddress: builder.mutation<AddressValidationResult, AddressValidationRequest>({
      queryFn: async request => {
        const service = getAddressValidationService();
        const result = await service.validateAddress(request);
        return { data: result };
      },
    }),

    validateCoordinates: builder.mutation<AddressValidationResult, CoordinatesValidationRequest>({
      queryFn: async request => {
        const service = getAddressValidationService();
        const result = await service.validateCoordinates(request);
        return { data: result };
      },
    }),

    reverseGeocode: builder.mutation<ReverseGeocodeResult | null, { lat: number; lon: number }>({
      queryFn: async ({ lat, lon }) => {
        const service = getAddressValidationService();
        const result = await service.reverseGeocode(lat, lon);
        return { data: result };
      },
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetAllOrdersQuery,
  useGetOrderByIdQuery,
  useGetOrdersByManufacturerIdQuery,
  useGetOrdersByDealerIdQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useUpdateTravelerCompletionMutation,
  useConfirmTrussStateChangeMutation,
  useConfirmAllTrussStateChangesMutation,
  useValidatePOUniquenessQuery,
  useGetBuildingsByOrderIdQuery,
  useGetBuildingByIdQuery,
  useAddBuildingToOrderMutation,
  useUpdateBuildingMutation,
  useDeleteBuildingMutation,
  useCloneBuildingMutation,
  useGetMonthlyDashboardQuery,
  useGetDashboardKpiQuery,
  useUpdateOrderDueDateMutation,
  usePlaceOrderOnHoldMutation,
  useShipOrderMutation,
  useReleaseForPickupMutation,
  useReleaseForShipmentMutation,
  useGetOrdersDueReportQuery,
  useGetOrdersPastDueReportQuery,
  useGetProductsReportQuery,
  useSignOrderMutation,
  useValidateCheckoutMutation,
  useCheckoutOrderMutation,
  useReturnOrderToDraftMutation,
  useReleaseToProductionMutation,
  useReleaseOrderFromHoldMutation,
  useGetAsceLoadRatingsMutation,
  useValidateAddressMutation,
  useValidateCoordinatesMutation,
  useReverseGeocodeMutation,
  usePrefetch,
} = ordersApi;
